/* Southern Atelier – product page: variant selection, size sheet, gallery thumbs, quantity bundle, add to bag */
import { saAddToCart } from './sa-cart.js';
import { CartLinesUpdateEvent } from '@shopify/events';

const root = () => window.Shopify?.routes?.root || '/';

/* Adds several variants at once (bundle). Kept here so it never depends on a cached copy of sa-cart.js. */
async function addLines(items, source) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: items.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
    promise: deferred.promise,
  }));
  const sections = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean).join(',');
  try {
    const res = await fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items.map((l) => ({ id: Number(l.id), quantity: l.quantity })), sections }),
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');
    const cart = await fetch(root() + 'cart.js').then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'sa', itemCount: items.reduce((n, l) => n + l.quantity, 0), sections: data.sections, didError: false },
    });
    window.saCart?.open?.();
  } catch (err) {
    deferred.resolve({ detail: { didError: true } });
    throw err;
  }
}

/* Shopify money format ("${{amount}}", "{{amount_with_comma_separator}} €", …) */
function formatMoney(cents, format) {
  const fmt = format || '${{amount}}';
  const split = (n, dec, t, d) => {
    const [i, f] = (n / 100).toFixed(dec).split('.');
    return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? d + f : '');
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return split(cents, 0, ',', '.');
      case 'amount_with_comma_separator': return split(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return split(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return split(cents, 2, "'", '.');
      default: return split(cents, 2, ',', '.');
    }
  });
}

class SaProduct {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-sa-form]');
    this.idInput = root.querySelector('[data-sa-variant-id]');
    this.addBtn = root.querySelector('[data-sa-add]');
    this.error = root.querySelector('[data-sa-error]');
    try { this.variants = JSON.parse(root.querySelector('[data-sa-variants]')?.textContent || '[]'); } catch (e) { this.variants = []; }
    this.fieldsets = [...root.querySelectorAll('[data-sa-option]')];
    this.sizeFs = this.fieldsets.find((f) => f.dataset.kind === 'size');
    this.needsSize = !!this.sizeFs && !this.sizeFs.querySelector('input:checked');
    this.bindOptions();
    this.bindSize();
    this.bindGallery();
    this.bindDrawers();
    this.bindBundle();
    this.bindSubmit();
    this.refresh(false);
    this.filterByColor(true);
  }

  selected() {
    return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value ?? null);
  }

  match(values) {
    return this.variants.find((v) => v.options.every((o, i) => values[i] == null || o === values[i]));
  }

  bindOptions() {
    this.fieldsets.forEach((fs) => fs.addEventListener('change', () => {
      if (fs === this.sizeFs) this.needsSize = false;
      this.refresh(true);
      if (fs.dataset.kind === 'swatch') this.filterByColor(false);
    }));
  }

  /* All images stay visible; selecting a colour jumps to that colour's first image */
  filterByColor(initial) {
    const gallery = this.root.querySelector('[data-sa-gallery]');
    const idx = Number(gallery?.dataset.colorIndex ?? -1);
    if (!gallery || idx < 0 || initial) return;
    const colour = this.selected()[idx];
    const slides = [...this.root.querySelectorAll('.sa-pdp__slide')];
    const i = slides.findIndex((s) => s.dataset.color === colour);
    if (i > -1) gallery.goTo?.(i);
  }

  refresh(fromUser) {
    const values = this.selected();
    const full = values.every((v) => v != null);
    const variant = full ? this.match(values) : null;

    // availability per value (given the other selected options)
    this.fieldsets.forEach((fs, i) => {
      fs.querySelectorAll('input').forEach((input) => {
        const test = values.slice();
        test[i] = input.value;
        const v = this.variants.find((x) => x.options.every((o, j) => test[j] == null || o === test[j]));
        const available = !!v && v.available;
        const label = input.closest('label');
        label?.classList.toggle('is-unavailable', !available);
        if (fs.dataset.kind === 'size') {
          const meta = label.querySelector('[data-sa-size-meta]');
          if (meta) meta.textContent = !v || !v.available ? this.addBtn.dataset.labelSoldout : (v.qty != null && v.qty > 0 && v.qty <= 3 ? `${v.qty} left` : '');
        }
      });
      const out = fs.querySelector('[data-sa-option-value]');
      if (out) out.textContent = values[i] ?? '';
    });

    this.root.classList.toggle('is-size-chosen', !this.needsSize);
    const sizeLabel = this.root.querySelector('[data-sa-size-label]');
    if (sizeLabel) sizeLabel.textContent = this.needsSize ? this.addBtn.dataset.labelChoose : (values[this.fieldsets.indexOf(this.sizeFs)] ?? this.addBtn.dataset.labelChoose);

    if (variant) {
      this.idInput.value = variant.id;
      const price = this.root.querySelector('[data-sa-price]');
      const save = variant.compare && variant.compareCents > variant.cents
        ? ` <span class="sa-price__save">Save ${formatMoney(variant.compareCents - variant.cents, price?.dataset.moneyFormat)}</span>`
        : '';
      if (price) price.innerHTML = variant.compare
        ? `<s class="sa-price__compare">${variant.compare}</s> <span class="sa-price__sale">${variant.price}</span>${save}`
        : `<span class="sa-price__regular">${variant.price}</span>`;
      if (fromUser) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
        if (variant.media) this.showMedia(variant.media);
      }
    }

    // the first bundle item follows the main selection
    if (this.bundleRows?.length) {
      this.bundleRows[0] = this.mainToBundle(values);
      this.renderBundleItems();
    }
    this.updateBundlePrices(variant);
    this.updateButton(variant);
  }

  updateButton(variant = this.match(this.selected())) {
    const b = this.addBtn;
    if (this.tier > 1) { b.disabled = false; b.textContent = b.dataset.labelAdd; b.dataset.mode = 'bundle'; }
    else if (this.needsSize) { b.disabled = false; b.textContent = b.dataset.labelChoose; b.dataset.mode = 'choose'; }
    else if (!variant || !variant.available) { b.disabled = true; b.textContent = b.dataset.labelSoldout; b.dataset.mode = 'soldout'; }
    else { b.disabled = false; b.textContent = b.dataset.labelAdd; b.dataset.mode = 'add'; }
  }

  /* ---------- quantity bundle: 1 / 2 / 3 items, each with its own colour & size ---------- */
  bindBundle() {
    this.bundle = this.root.querySelector('[data-sa-bundle]');
    this.tier = 1;
    if (!this.bundle) return;
    this.bundleItems = this.bundle.querySelector('[data-sa-bundle-items]');
    // options + variants offered in the bundle rows: this product, or every colour of its colour group
    let data = {};
    try { data = JSON.parse(this.bundle.querySelector('[data-sa-bundle-data]')?.textContent || '{}'); } catch (e) {}
    this.bundleData = data;
    this.bundleOptions = data.options || [];
    this.bundleVariants = data.variants || [];
    this.bundleColor = this.bundleOptions.findIndex((o) => /colou?r|colore/i.test(o.name));
    this.bundleRows = [];
    this.bundle.querySelectorAll('input[type="radio"]').forEach((input) => input.addEventListener('change', () => {
      this.tier = Number(input.value) || 1;
      const main = this.mainToBundle();
      this.bundleRows = this.tier > 1 ? Array.from({ length: this.tier }, (_, i) => (this.bundleRows[i] || main).slice()) : [];
      if (this.tier > 1) this.bundleRows[0] = main.slice();
      this.renderBundleItems();
      this.updateButton();
      this.error.hidden = true;
    }));
  }

  /* the main selection expressed in the bundle's options (grouped products add their own colour) */
  mainToBundle(values = this.selected()) {
    const v = values.every((x) => x != null) ? this.match(values) : null;
    const bv = v && this.bundleVariants.find((x) => x.id === v.id);
    if (bv) return bv.options.slice();
    if (this.bundleData.grouped) return [this.bundleData.self, values[0] ?? null];
    return values.slice();
  }

  renderBundleItems() {
    const wrap = this.bundleItems;
    if (!wrap) return;
    wrap.hidden = this.tier < 2;
    wrap.innerHTML = '';
    if (this.tier < 2) return;
    const opts = this.bundleOptions;
    this.bundleRows.forEach((values, r) => {
      const row = document.createElement('div');
      row.className = 'sa-bundle__row';
      row.style.setProperty('--cols', String(opts.length));
      const label = document.createElement('span');
      label.className = 'sa-bundle__row-label';
      label.textContent = `#${r + 1}`;
      // picture of the chosen colour
      const thumb = document.createElement('span');
      thumb.className = 'sa-bundle__thumb';
      const match = this.bundleVariants.find((x) => x.thumb && x.options.every((o, j) => values[j] == null || o === values[j]))
        || (this.bundleColor > -1 && this.bundleVariants.find((x) => x.thumb && x.options[this.bundleColor] === values[this.bundleColor]));
      if (match) {
        const img = document.createElement('img');
        img.src = match.thumb;
        img.alt = values.filter(Boolean).join(' / ');
        img.loading = 'lazy';
        thumb.append(img);
      }
      row.append(label, thumb);
      opts.forEach((opt, i) => {
        const select = document.createElement('select');
        select.setAttribute('aria-label', `${this.bundle.dataset.itemLabel || 'Item'} ${r + 1} – ${opt.name}`);
        const ph = document.createElement('option');
        ph.value = '';
        ph.textContent = opt.name;
        ph.disabled = true;
        select.append(ph);
        opt.values.forEach((value) => {
          const o = document.createElement('option');
          o.value = value;
          o.textContent = value;
          // sold out: a colour with no size left, or a size not available in the chosen colour
          const test = this.bundleOptions.map((_, j) => (j === i ? value : (i !== this.bundleColor && j === this.bundleColor ? values[j] : null)));
          const anyAvailable = this.bundleVariants.some((x) => x.available && x.options.every((ov, j) => test[j] == null || ov === test[j]));
          if (!anyAvailable) { o.textContent = `${value} – sold out`; o.disabled = true; }
          select.append(o);
        });
        select.value = values[i] ?? '';
        select.classList.toggle('is-empty', !values[i]);
        select.addEventListener('change', () => {
          this.bundleRows[r][i] = select.value;
          // new colour without the chosen size → keep the size only if it exists there
          const ok = this.bundleVariants.some((x) => x.available && x.options.every((ov, j) => this.bundleRows[r][j] == null || ov === this.bundleRows[r][j]));
          if (!ok) this.bundleRows[r] = this.bundleRows[r].map((val, j) => (j === i ? val : null));
          this.renderBundleItems();
        });
        row.append(select);
      });
      wrap.append(row);
    });
  }

  updateBundlePrices(variant) {
    if (!this.bundle) return;
    const v = variant || this.variants.find((x) => x.available) || this.variants[0];
    if (!v || v.cents == null) return;
    const fmt = (c) => formatMoney(c, this.bundle.dataset.moneyFormat);
    const pcts = { 1: 0, 2: Number(this.bundle.dataset.pct2) || 0, 3: Number(this.bundle.dataset.pct3) || 0 };
    this.bundle.querySelectorAll('[data-sa-tier]').forEach((tier) => {
      const n = Number(tier.dataset.saTier);
      const total = v.cents * n;
      const now = Math.round((total * (100 - pcts[n])) / 100);
      const old = tier.querySelector('[data-old]');
      if (old) old.textContent = fmt(total);
      tier.querySelector('[data-new]').textContent = fmt(now);
    });
  }

  bundleLines() {
    // product without options: just N pieces of the same variant
    if (!this.bundleVariants.length) return { missing: -1, items: [{ id: Number(this.idInput.value), quantity: this.tier }] };
    const lines = new Map();
    let missing = -1;
    this.bundleRows.forEach((values, r) => {
      const v = values.every((x) => x) ? this.bundleVariants.find((x) => x.options.every((o, i) => o === values[i])) : null;
      if (!v || !v.available) { if (missing < 0) missing = r; return; }
      lines.set(v.id, (lines.get(v.id) || 0) + 1);
    });
    return { missing, items: [...lines].map(([id, quantity]) => ({ id, quantity })) };
  }

  bindSize() {
    if (!this.sizeFs) return;
    const row = this.sizeFs.querySelector('[data-sa-size-row]');
    const hint = this.sizeFs.querySelector('[data-sa-size-hint]');
    this.openSize = () => {
      row?.classList.add('is-required');
      if (hint) hint.hidden = false;
      this.sizeFs.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.sizeFs.querySelector('input')?.focus({ preventScroll: true });
    };
    this.sizeFs.addEventListener('change', () => {
      row?.classList.remove('is-required');
      if (hint) hint.hidden = true;
    });
  }

  bindGallery() {
    const main = this.root.querySelector('[data-sa-gallery]');
    const thumbs = [...this.root.querySelectorAll('[data-sa-thumb]')];
    if (!main) return;
    thumbs.forEach((t) => t.addEventListener('click', () => {
      main.goTo?.(Number(t.dataset.saThumb));
      thumbs.forEach((x) => x.classList.toggle('is-active', x === t));
    }));
    main.addEventListener('sa:slide', () => {
      const track = main.querySelector('[data-sa-track]');
      const visible = [...this.root.querySelectorAll('.sa-pdp__slide')].filter((s) => !s.hidden);
      const i = Math.round(track.scrollLeft / (visible[0]?.offsetWidth || 1));
      const cur = visible[i];
      const all = [...this.root.querySelectorAll('.sa-pdp__slide')];
      thumbs.forEach((t) => t.classList.toggle('is-active', all[Number(t.dataset.saThumb)] === cur));
    });
  }

  showMedia(mediaId) {
    const main = this.root.querySelector('[data-sa-gallery]');
    const slides = [...this.root.querySelectorAll('.sa-pdp__slide')];
    const i = slides.findIndex((s) => s.dataset.mediaId == mediaId);
    if (i > -1) main?.goTo?.(i);
  }

  bindDrawers() {
    this.root.querySelectorAll('[data-sa-drawer-open]').forEach((btn) => btn.addEventListener('click', () => {
      const d = this.root.querySelector(`[data-sa-drawer="${btn.dataset.saDrawerOpen}"]`);
      if (!d) return;
      d.hidden = false; document.documentElement.classList.add('sa-lock');
      d.querySelector('[data-sa-drawer-close]')?.focus();
      const close = () => { d.hidden = true; document.documentElement.classList.remove('sa-lock'); btn.focus(); };
      d.querySelectorAll('[data-sa-drawer-close]').forEach((c) => (c.onclick = close));
      d.onkeydown = (e) => { if (e.key === 'Escape') close(); };
    }));
  }

  bindSubmit() {
    this.form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const b = this.addBtn;
      let payload = { id: this.idInput.value, quantity: 1, source: this.form };
      if (b.dataset.mode === 'bundle') {
        const { missing, items } = this.bundleLines();
        if (missing > -1) {
          const row = this.bundleItems?.children[missing];
          row?.classList.add('is-missing');
          row?.querySelector('select.is-empty, select')?.focus();
          this.error.textContent = `Please choose the options for item #${missing + 1}.`;
          this.error.hidden = false;
          return;
        }
        payload = { items };
      } else if (b.dataset.mode === 'choose') { this.openSize?.(); return; }
      if (b.disabled) return;
      this.error.hidden = true;
      b.setAttribute('aria-busy', 'true');
      try {
        if (payload.items) await addLines(payload.items, this.form);
        else await saAddToCart(payload);
        b.textContent = b.dataset.labelAdded;
        setTimeout(() => this.updateButton(), 1800);
      } catch (err) {
        this.error.textContent = err.message;
        this.error.hidden = false;
      } finally {
        b.removeAttribute('aria-busy');
      }
    });
  }
}

const boot = () => document.querySelectorAll('[data-sa-product]').forEach((el) => { if (!el.__sa) el.__sa = new SaProduct(el); });
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
document.addEventListener('shopify:section:load', boot);
